import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectVendorsAPI, projectTagsAPI, reportsAPI } from '../services/api';

// ==================== Query Keys ====================

export const projectVendorKeys = {
  all: (projectId) => ['projects', projectId, 'vendors'],
  list: (projectId, filters) => [...projectVendorKeys.all(projectId), 'list', filters],
  tags: (projectId) => ['projects', projectId, 'tags'],
};

export const reportKeys = {
  all: ['reports'],
  list: () => [...reportKeys.all, 'list'],
  detail: (id) => [...reportKeys.all, 'detail', id],
  data: (id, params) => [...reportKeys.detail(id), 'data', params],
};

// ==================== Project Vendor Queries ====================

/**
 * 取得專案供應商列表
 */
export function useProjectVendors(projectId, filters = {}, options = {}) {
  return useQuery({
    queryKey: projectVendorKeys.list(projectId, filters),
    queryFn: () => projectVendorsAPI.getAll(projectId, filters),
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000, // 2 分鐘
    ...options,
  });
}

/**
 * 取得專案內標籤列表
 */
export function useProjectTags(projectId, options = {}) {
  return useQuery({
    queryKey: projectVendorKeys.tags(projectId),
    queryFn: () => projectTagsAPI.getAll(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// ==================== Report Queries ====================

/**
 * 取得報表列表
 */
export function useReports(options = {}) {
  return useQuery({
    queryKey: reportKeys.list(),
    queryFn: () => reportsAPI.getAll(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * 取得報表資料
 */
export function useReportData(reportId, params = {}, options = {}) {
  return useQuery({
    queryKey: reportKeys.data(reportId, params),
    queryFn: () => reportsAPI.getData(reportId, params),
    enabled: !!reportId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// ==================== Mutations ====================

/**
 * 從報表匯入供應商
 */
export function useImportVendors(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, reportId, vendorIds, tags }) =>
      projectVendorsAPI.importFromReport(projectId, reportId, vendorIds, tags),
    onSuccess: (data, variables) => {
      console.log('[useImportVendors] 匯入成功，正在更新快取...', {
        projectId: variables.projectId,
        imported: data.imported,
      });
      // 直接 refetch 而不是 invalidate
      queryClient.refetchQueries({
        queryKey: projectVendorKeys.all(variables.projectId),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 批量標籤操作
 */
export function useBulkTag(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, vendorIds, tags, action }) =>
      projectVendorsAPI.bulkTag(projectId, vendorIds, tags, action),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.all(variables.projectId),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 更新供應商
 */
export function useUpdateVendor(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, vendorId, data }) =>
      projectVendorsAPI.update(projectId, vendorId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.all(variables.projectId),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 刪除供應商
 */
export function useDeleteVendor(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, vendorId }) => {
      const result = await projectVendorsAPI.delete(projectId, vendorId);
      return result;
    },
    onSuccess: (data, variables) => {
      // 使用 predicate 匹配所有包含此專案供應商的查詢
      queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) &&
            key[0] === 'projects' &&
            key[1] === variables.projectId &&
            key[2] === 'vendors';
        },
      });
      options.onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      options.onError?.(error, variables);
    },
    ...options,
  });
}

/**
 * 建立專案標籤
 */
export function useCreateProjectTag(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, name, color }) =>
      projectTagsAPI.create(projectId, { name, color }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.tags(variables.projectId),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 更新專案標籤
 */
export function useUpdateProjectTag(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, tagId, data }) =>
      projectTagsAPI.update(projectId, tagId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.tags(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.all(variables.projectId),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * 刪除專案標籤
 */
export function useDeleteProjectTag(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, tagId }) =>
      projectTagsAPI.delete(projectId, tagId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.tags(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: projectVendorKeys.all(variables.projectId),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}
